'use client';

import Link from 'next/link';
import { TbFileInvoice } from 'react-icons/tb';

interface QuoteButtonProps {
    locale: string;
    /** Optional service slug to pre-select on the quote form. */
    service?: string;
    /** Visual size — nav uses 'sm', hero/sections use 'md' or 'lg'. */
    size?: 'sm' | 'md' | 'lg';
    /** 'solid' (filled red CTA) or 'outline' (subtle, for busy backgrounds). */
    variant?: 'solid' | 'outline';
    className?: string;
    label?: string;
    showIcon?: boolean;
    onClick?: () => void;
}

const sizeClasses: Record<NonNullable<QuoteButtonProps['size']>, string> = {
    sm: 'px-4 py-2 text-sm gap-1.5',
    md: 'px-6 py-3 text-base gap-2',
    lg: 'px-8 py-4 text-lg gap-2.5',
};

/**
 * Site-wide "Get a Quote" CTA. Routes to the contact page in quote mode
 * (?intent=quote), optionally pre-selecting a service, so every quote entry
 * point lands on the same expanded brief form.
 */
export default function QuoteButton({
    locale,
    service,
    size = 'md',
    variant = 'solid',
    className = '',
    label,
    showIcon = true,
    onClick,
}: QuoteButtonProps) {
    const isEn = locale === 'en';
    const text = label ?? (isEn ? 'Get a Quote' : 'اطلب عرض سعر');

    const query = new URLSearchParams({ intent: 'quote' });
    if (service) query.set('service', service);
    const href = `/${locale}/contact?${query.toString()}`;

    const base =
        'inline-flex items-center justify-center rounded-full font-heading font-bold transition-all duration-300';
    const variantClass =
        variant === 'solid'
            ? 'bg-brand-cta text-white shadow-[0_10px_30px_rgba(234,50,40,0.28)] hover:bg-white hover:text-brand-cta'
            : 'border border-brand-text/40 text-brand-text hover:border-brand-cta hover:text-brand-cta hover:bg-brand-cta/5';

    return (
        <Link
            href={href}
            onClick={onClick}
            className={`${base} ${sizeClasses[size]} ${variantClass} hover:scale-[1.03] ${className}`}
        >
            {showIcon && <TbFileInvoice className="h-[1.1em] w-[1.1em]" aria-hidden="true" />}
            <span>{text}</span>
        </Link>
    );
}
